import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Snackbar, 
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface FreeAccessCheckProps {
  children: React.ReactNode;
}

const FreeAccessCheck: React.FC<FreeAccessCheckProps> = ({ children }) => {
  const { isAuthenticated, userSubscription } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [showLoginSuggestion, setShowLoginSuggestion] = useState<boolean>(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        setLoading(true);
        
        // If user is authenticated, they can access based on their subscription
        if (isAuthenticated) {
          setLoading(false);
          return;
        }
        
        // For non-authenticated users, show login suggestion after a delay
        setTimeout(() => {
          setShowLoginSuggestion(true);
        }, 30000); // Show login suggestion after 30 seconds
        
      } catch (err) {
        console.error('Error checking access:', err);
      } finally {
        setLoading(false);
      }
    };
    
    checkAccess();
  }, [isAuthenticated, userSubscription]);
  
  const handleLoginClick = () => {
    navigate('/signup');
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body1">Checking access...</Typography>
      </Box>
    );
  }
  
  return (
    <>
      {/* Login suggestion for new users */}
      <Snackbar
        open={showLoginSuggestion}
        autoHideDuration={10000}
        onClose={() => setShowLoginSuggestion(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="info" 
          sx={{ 
            width: '100%', 
            alignItems: 'center',
            '& .MuiAlert-message': { display: 'flex', alignItems: 'center' }
          }}
          action={
            <>
              <Button 
                color="primary" 
                size="small" 
                onClick={handleLoginClick}
                sx={{ mr: 1 }}
              >
                Login
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setShowLoginSuggestion(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        >
          Login to access more features and get 5 free capsules per month!
        </Alert>
      </Snackbar>
      
      {children}
    </>
  );
};

export default FreeAccessCheck;
